import { cn } from '@/lib/cn';
import { formatCurrency } from '@/lib/format';

export interface BarSeries {
  name: string;
  /** classe Tailwind de preenchimento (ex.: `bg-viz-received`) */
  className: string;
  values: number[];
  /** previsto (mais claro, tracejado) */
  dashed?: boolean;
}

export interface LineSeries {
  name: string;
  values: (number | null)[];
}

const compact = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
const axis = (cents: number) => `R$ ${compact.format(cents / 100)}`;

/**
 * Gráfico de barras agrupadas (HTML) com linha opcional (SVG sem texto). Eixo sempre parte do zero;
 * cada coluna tem rótulo e valores em `aria-label`/`title`, e há uma tabela só para leitores de tela.
 */
export function BarChart({ labels, series, line, ariaLabel, testId, height = 192 }: { labels: string[]; series: BarSeries[]; line?: LineSeries; ariaLabel: string; testId?: string; height?: number }) {
  const n = labels.length;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const lineVals = (line?.values ?? []).filter((v): v is number => v != null);
  const lMin = Math.min(0, ...lineVals);
  const lMax = Math.max(1, ...lineVals);
  const step = Math.max(1, Math.ceil(n / 8));
  const lineY = (v: number) => 100 - ((v - lMin) / (lMax - lMin || 1)) * 100;
  const points = line ? line.values.map((v, i) => (v == null ? null : `${((i + 0.5) / n) * 100},${lineY(v)}`)).filter(Boolean) : [];
  const empty = series.every((s) => s.values.every((v) => v === 0)) && lineVals.length === 0;

  return (
    <figure className="relative m-0" data-testid={testId}>
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-hidden>
        {series.map((s) => (
          <span key={s.name} className="inline-flex items-center gap-1.5">
            <span className={cn('inline-block size-3 rounded-[3px]', s.className, s.dashed && 'opacity-45')} />
            {s.name}
          </span>
        ))}
        {line && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 bg-foreground" />
            {line.name}
          </span>
        )}
      </div>
      <div className="relative flex gap-2" role="img" aria-label={ariaLabel}>
        <div className="flex w-12 shrink-0 flex-col justify-between pb-6 text-right text-[10px] text-muted-foreground tabular" style={{ height }} aria-hidden>
          <span>{axis(max)}</span>
          <span>R$ 0</span>
        </div>
        <div className="relative min-w-0 flex-1">
          <div className="relative flex items-end border-b" style={{ height: height - 24 }}>
            {labels.map((l, i) => (
              <div key={`${l}${i}`} tabIndex={0} className="group flex h-full min-w-0 flex-1 items-end justify-center gap-px px-px outline-none focus-visible:bg-muted/60 hover:bg-muted/40" aria-label={`${l}: ${series.map((s) => `${s.name} ${formatCurrency(s.values[i] ?? 0)}`).join('; ')}`} title={`${l}\n${series.map((s) => `${s.name}: ${formatCurrency(s.values[i] ?? 0)}`).join('\n')}`}>
                {series.map((s) => {
                  const v = s.values[i] ?? 0;
                  return <span key={s.name} className={cn('w-full max-w-5 rounded-t-[3px]', s.className, s.dashed && 'opacity-45')} style={{ height: `${v > 0 ? Math.max((v / max) * 100, 1.5) : 0}%` }} />;
                })}
              </div>
            ))}
            {line && points.length > 1 && (
              <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                <polyline points={points.join(' ')} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" className="text-foreground" />
              </svg>
            )}
          </div>
          <div className="flex h-6 pt-1.5" aria-hidden>
            {labels.map((l, i) => (
              <div key={`x${l}${i}`} className="relative min-w-0 flex-1">
                {i % step === 0 && <span className="absolute left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap text-muted-foreground">{l}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
      {empty && <p className="mt-2 text-sm text-muted-foreground">Sem movimentação neste período.</p>}
      <div className="sr-only">
      <table>
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th>Período</th>
            {series.map((s) => (
              <th key={s.name}>{s.name}</th>
            ))}
            {line && <th>{line.name}</th>}
          </tr>
        </thead>
        <tbody>
          {labels.map((l, i) => (
            <tr key={l + i}>
              <td>{l}</td>
              {series.map((s) => (
                <td key={s.name}>{formatCurrency(s.values[i] ?? 0)}</td>
              ))}
              {line && <td>{line.values[i] == null ? '' : formatCurrency(line.values[i]!)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </figure>
  );
}
